import {
  CheckCircleOutlined,
  DeleteOutlined,
  EditOutlined,
  FolderAddOutlined,
  StopOutlined,
} from "@ant-design/icons";
import { Table, Space, Tooltip, Pagination, Tag, Popover } from "antd";
import React, { useEffect, useState } from "react";
import ConfirmationModal from "../modal/ConfirmationModal";
import {
  openNotificationError,
  openNotificationSuccess,
} from "../../helpers/NotificationHelpers";
import EditPlanAttributeModal from "../modal/edit/EditPlanAttributeModal";
import DisablePlanAttributeApi from "../../api/attributes/DisablePlanAtrributeApi";
import EnablePlanAttributeApi from "../../api/attributes/EnablePlanAttributeApi";
import DeletePlanAttributeApi from "../../api/attributes/DeletePlanAttributeApi";
import { useSelector } from "react-redux";
import { permissionAbility } from "../../helpers/permissionHelpers";
import { LIMIT_PER_PAGE } from "../../helpers/constants";

function PlanAttributesTable(props) {
  const [editModal, setEditModal] = useState(false);
  const [deleteModal, setDeleteModal] = useState("");
  const [disableModal, setDisableModal] = useState(false);
  const [disableAttributeId, setDisableAttributeId] = useState("");
  const [enableModal, setEnableModal] = useState(false);
  const [enableAttributeId, setEnableAttributeId] = useState("");
  const [deleteAttributeId, setDeleteAttributeId] = useState("");
  const [editPlanAttribute, setEditPlanAttribute] = useState();
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const positionState = useSelector((state) => state.positionState.value);
  const userLevel = positionState?.userRole?.name;

  useEffect(() => {
    setLoading(props.loading);
  }, [props.loading]);

  useEffect(() => {
    setPage(props.page);
  }, [props.page]);

  async function disableAttribute() {
    const apiResponse = await DisablePlanAttributeApi(disableAttributeId);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Plan attribute disabled");
      props.changePage(page);
    } else {
      openNotificationError(
        "bottomRight",
        "Couldn't disable. Try again later."
      );
    }
    setDisableAttributeId("");
  }

  async function enableAttribute() {
    const apiResponse = await EnablePlanAttributeApi(enableAttributeId);
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", "Plan attribute enabled");
      props.changePage(page);
    } else {
      openNotificationError("bottomRight", "Couldn't enable. Try again later.");
    }
    setEnableAttributeId("");
  }

  async function deleteAttribute() {
    const apiResponse = await DeletePlanAttributeApi(deleteAttributeId);
    
    if (apiResponse.error) {
      openNotificationError("bottomRight", apiResponse.message);
    } else if (apiResponse.success) {
      openNotificationSuccess("bottomRight", apiResponse.message);
      if (props.data.planAttributes?.length === 1) {
        props.changePage(page - 1);
      } else {
        props.changePage(page);
      }
    } else {
      openNotificationError("bottomRight", "Couldn't delete. Try again later.");
    }
    setDeleteAttributeId("");
  }

  function getColorStatus(status) {
    if (status === "Active") {
      return "green";
    } else {
      return "red";
    }
  }

  const columns = [
    {
      title: "Attribute name",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div className="flex items-center gap-1">
          {record.name}
          {permissionAbility(userLevel, "plan-attributes", "edit") && (
            <Tooltip title={"Edit name"}>
              <div
                onClick={() => {
                  setEditModal(true);
                  setEditPlanAttribute(record);
                }}
                className="text-blue-500 hover:text-blue-600 cursor-pointer text-xs"
              >
                <EditOutlined />
              </div>
            </Tooltip>
          )}
        </div>
      ),
    },
    {
      title: "Value type",
      dataIndex: "valueType",
      key: "valueType",
      render: (text, record) => (
        <div>
          {record.valueType?.charAt(0).toUpperCase() +
            record.valueType?.slice(1)}
        </div>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (text, record) => (
        <Tag color={getColorStatus(text)}>{record.status?.toUpperCase()}</Tag>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Space size="middle">
          {permissionAbility(userLevel, "plan-attributes", "edit") &&
            (record.status === "Active" ? (
              <Tooltip title={"Disable"}>
                <div
                  onClick={() => {
                    setDisableModal(true);
                    setDisableAttributeId(record.id);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <StopOutlined />
                </div>
              </Tooltip>
            ) : (
              <Tooltip title={"Enable"}>
                <div

                  onClick={() => {
                    setEnableModal(true);
                    setEnableAttributeId(record.id);
                  }}
                  className="text-green-500 hover:text-green-600 cursor-pointer"
                >
                  <CheckCircleOutlined />
                </div>
              </Tooltip>
            ))}
          {permissionAbility(userLevel, "plan-attributes", "delete") &&
            (record.canDelete ? (
              <Tooltip title={"Delete"}>
                <div
                  onClick={() => {
                    setDeleteModal("delete");
                    setDeleteAttributeId(record.id);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <DeleteOutlined />
                </div>
              </Tooltip>
            ) : record.canArchive ? (
              <Tooltip title={"Archive"}>
                <div
                  onClick={() => {
                    setDeleteModal("archive");
                    setDeleteAttributeId(record.id);
                  }}
                  className="text-red-500 hover:text-red-600 cursor-pointer"
                >
                  <FolderAddOutlined />
                </div>
              </Tooltip>
            ) : (
              <Popover
                placement="left"
                content={"This plan attribute can't be deleted"}
                trigger="hover"
              >
                <div className="text-gray-500">
                  <FolderAddOutlined />
                </div>
              </Popover>
            ))}
        </Space>
      ),
    },
  ];

  return (
    <>
      <Table
        columns={columns}
        dataSource={props.data?.planAttributes}
        pagination={false}
        loading={loading}
      />
      <div className="text-center mt-6">
        <Pagination
          onChange={(num) => props.changePage(num)}
          current={page}
          total={props.data?.totalCount || 0}
          pageSize={LIMIT_PER_PAGE}
        />
      </div>
      <EditPlanAttributeModal
        visible={editModal}
        handleCancel={() => {
          setEditModal(false);
          setEditPlanAttribute();
        }}
        planAttribute={editPlanAttribute}
        handleOk={() => props.changePage(page)}
      />
      <ConfirmationModal
        visible={disableModal}
        handleCancel={() => {
          setDisableModal(false);
          setDisableAttributeId("");
        }}
        handleOk={() => disableAttribute()}
        text="Are you sure want to disable this plan attribute?"
        title="Disable plan attribute"
      />
      <ConfirmationModal
        visible={enableModal}
        handleCancel={() => {
          setEnableModal(false);
          setEnableAttributeId("");
        }}
        handleOk={() => enableAttribute()}
        text="Are you sure want to enable this plan attribute?"
        title="Enable plan attribute"
      />
      <ConfirmationModal
        visible={deleteModal}
        handleCancel={() => {
          setDeleteModal("");
          setDeleteAttributeId("");
        }}
        handleOk={() => deleteAttribute()}
        text={`Are you sure want to ${deleteModal} this attribute?`}
        title={deleteModal?.charAt(0).toUpperCase() + deleteModal?.slice(1)}
      />
    </>
  );
}

export default PlanAttributesTable;
